import { LexicalEditor } from 'lexical';
import { RefObject, useMemo, useRef } from 'react';
import * as Constants from 'const';
import { BASE_PROPS_COMMAND, BaseProps } from '../Plugins/BasePropsPlugin';
import { LINK_COMMAND, LinkProps } from '../Plugins/LinkNodePlugin';
import { NoWrapProps, TEXT_NOWRAP_COMMAND } from '../Plugins/NoWrapPlugin';
import { REFERENCES_FONT_COMMAND, ReferencesFontProps } from '../Plugins/ReferencesFontPlugin';
import { usePropsUpdater } from './usePropsUpdater';

export type LexicalOnChange = {
  baseProps: (values: BaseProps) => void;
  font: (values: ReferencesFontProps) => void;
  link: (values: LinkProps) => void;
  textContent: (value: string) => void;
  textNoWrap: (values: NoWrapProps) => void;
};

type Props = {
  charStyle: Constants.CharStyle[];
  link: string | undefined;
  linkApplicable: boolean;
  scriptStyle?: Constants.ScriptType;
  hasTextContent: boolean;
  textNoWrap: boolean;
  selectionCollapsed: boolean;
};

type Setters = {
  charStyle: (style: Constants.CharStyle) => void; // will toggle one of inline styles
  link: (value: string | undefined) => void;
  scriptStyle: (style: Constants.ScriptType) => void;
  textNoWrap: () => void;
  clearFormatting: () => void;
};

type LexicalReferencesEditorHook = {
  disableLink: boolean;
  fontStyles: string[];
  isNoWrapActive: boolean;
  isNoWrapButtonDisabled: boolean;
  link: string;
  toggleLink: (link: string) => void;
  toggleNoWrap: () => void;
  clearFormatting: () => void;
  toggleCharStyle: (style: Constants.CharStyle) => void;
  toggleScriptStyle: (style: Constants.ScriptType) => void;
  ref: RefObject<LexicalEditor>;
  onChange: LexicalOnChange;
  hasTextContent: boolean;
};

export function useLexicalReferencesEditor(): LexicalReferencesEditorHook {
  const ref = useRef<LexicalEditor>(null);

  const { props, updateProps } = usePropsUpdater<Props>({
    charStyle: [],
    link: undefined,
    linkApplicable: false,
    scriptStyle: undefined,
    hasTextContent: false,
    textNoWrap: false,
    selectionCollapsed: true,
  });

  const setters = useMemo((): Setters => ({
    charStyle: (style: Constants.CharStyle) => ref.current?.dispatchCommand(
      REFERENCES_FONT_COMMAND.CHAR_STYLE,
      style,
    ),
    link: (value: string | undefined) => ref.current?.dispatchCommand(LINK_COMMAND, value),
    scriptStyle: (value: Constants.ScriptType) => ref.current?.dispatchCommand(BASE_PROPS_COMMAND.SCRIPT_STYLE, value),
    textNoWrap: () => ref.current?.dispatchCommand(TEXT_NOWRAP_COMMAND, undefined),
    clearFormatting: () => ref.current?.dispatchCommand(BASE_PROPS_COMMAND.CLEAR_FORMATTING, undefined),
  }), []);

  const onChange: LexicalOnChange = useMemo(() => ({
    baseProps: (values: BaseProps) => updateProps({
      scriptStyle: values.scriptStyle,
    }),
    font: ({ charStyle }) => updateProps({ charStyle }),
    link: values => updateProps({
      link: values.link,
      linkApplicable: values.linkApplicable,
    }),
    textContent: value => updateProps({
      hasTextContent: Boolean(value.length),
    }),
    textNoWrap: values => updateProps({
      textNoWrap: values.textNoWrap,
      selectionCollapsed: values.selectionCollapsed,
    }),
  }), [updateProps]);

  return {
    disableLink: !props.linkApplicable,
    ref,
    fontStyles: [...props.charStyle, props.scriptStyle].filter(Boolean) as string[],
    isNoWrapActive: props.textNoWrap,
    isNoWrapButtonDisabled: props.selectionCollapsed,
    link: props.link ?? '',
    hasTextContent: props.hasTextContent,
    onChange,
    toggleLink: (link: string) => setters.link(link || undefined),
    toggleNoWrap: setters.textNoWrap,
    clearFormatting: setters.clearFormatting,
    toggleCharStyle: setters.charStyle,
    toggleScriptStyle: setters.scriptStyle,
  };
}
